import { api_configuration } from "../../common";
import api_cs = api_configuration.types.client_state.alan_api;
import api_views = api_configuration.libraries.gui_definition_lib.types.views.alan_api;
import api_cr = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.client_request.alan_api;
import api_collation_table = api_configuration.libraries.gui_definition_lib.types.collation_table.alan_api;
import NodePathBuilder from "../navigation/NodePathBuilder";
import { hasStyle } from "./style";
import { IdPathBuilder } from "../navigation/IdPathBuilder";
import { createIdPathBuilderFromNode } from "../navigation/createIdPathFromNode";

function createCharacterAlternatives(primary_character: api_collation_table.Cprimary) {
	return global.collation_table_inverse[primary_character.parent.key];
}

export function mapNodePath($: api_views.Cnode_path): NodePathBuilder {
	return buildNodePath($, new NodePathBuilder());
}

function buildNodePath(path: api_views.Cnode_path, builder: NodePathBuilder): NodePathBuilder {
	const $has_steps = path.properties.has_steps.state;
	switch ($has_steps.name) {
		case "no":
			return builder;
		case "yes": {
			const $type = $has_steps.node.properties.type.state;
			switch ($type.name) {
				case "parent":
					return buildNodePath($has_steps.node.properties.tail, builder.parent());
				case "state rule":
					return buildNodePath($has_steps.node.properties.tail, builder.state_rule($type.node.properties.rule.entry));
				case "reference rule":
					return buildNodePath(
						$has_steps.node.properties.tail,
						builder.reference_rule($type.node.properties.property.entry, $type.node.properties.rule.entry)
					);
				case "reference":
					return buildNodePath($has_steps.node.properties.tail, builder.reference($type.node.properties.text.entry));
				case "group":
					return buildNodePath($has_steps.node.properties.tail, builder.group($type.node.properties.group.entry));
				case "state":
					return buildNodePath(
						$has_steps.node.properties.tail,
						builder.state($type.node.properties.state_group.entry, $type.node.properties.state.entry)
					);
				case "collection entry":
					return buildNodePath(
						$has_steps.node.properties.tail,
						builder.collection_entry($type.node.properties.collection.entry, $type.node.properties.entry_key)
					);
				default:
					unexpectedState($type);
			}
			break;
		}
		default:
			unexpectedState($has_steps);
	}
}

type PropertiesCollection = api_cr.Tnode_query["properties"];
type Filters = NonNullable<api_cr.Tcollection_query["filters"]["value filters"]>;

function buildTextExpressionFromString(
	criterium: string,
	head: api_cr.Tfilter_expression | undefined = undefined,
	tail: api_cr.Tfilter_expression["has more steps"] = ["no", {}]
): [api_cr.Tfilter_expression | undefined, api_cr.Tfilter_expression["has more steps"]] {
	const string_left: string = criterium;
	const length = string_left.length;
	if (length === 0) {
		return [head, tail];
	}
	for (let i = string_left.length - 1; i >= 0; i--) {
		const character = string_left[i];
		if (character === "*") {
			head = {
				type: ["wildcard", {}],
				"has more steps": tail,
			};
			tail = [
				"yes",
				{
					tail: head,
				},
			];
		} else {
			const entry: api_collation_table.Ccharacters = global.collation_table.properties.characters.get(character);
			// eslint-disable-next-line @typescript-eslint/ban-types
			let alternatives: Record<string, {}> = {};
			if (entry !== undefined) {
				switch (entry.properties.type.state.name) {
					case "alternative": {
						const $ = entry.properties.type.state.node;
						alternatives = createCharacterAlternatives($.properties.for_.inferences.for_());
						break;
					}
					case "primary": {
						const $ = entry.properties.type.state.node;
						alternatives = createCharacterAlternatives($);
						break;
					}
					default:
						unexpectedState(entry.properties.type.state);
				}
			} else {
				alternatives = {
					[character]: {},
				};
			}
			head = {
				type: [
					"alternatives",
					{
						alternatives: alternatives,
					},
				],
				"has more steps": tail,
			};
			tail = [
				"yes",
				{
					tail: head,
				},
			];
		}
	}
	return [head, tail];
}

export function buildTextExpression(operation: api_cs.Cyes__has_filter__text): api_cr.Tfilter_expression {
	//create a tail based on the operation
	const string_left: string = operation.properties.value;
	let head: api_cr.Tfilter_expression | undefined;
	let tail: api_cr.Tfilter_expression["has more steps"] = ["no", {}];
	switch (operation.properties.operation.state.name) {
		case "contains": {
			tail = [
				"yes",
				{
					tail: {
						type: "wildcard",
						"has more steps": "no",
					},
				},
			];
			break;
		}
		case "ends with": {
			break;
		}
		case "equals": {
			break;
		}
		case "starts with": {
			tail = [
				"yes",
				{
					tail: {
						type: "wildcard",
						"has more steps": "no",
					},
				},
			];
			break;
		}
		default:
			unexpectedState(operation.properties.operation.state);
	}
	[head, tail] = buildTextExpressionFromString(string_left, head, tail);
	if (head === undefined) {
		head = {
			type: [
				"alternatives",
				{
					alternatives: {
						"": {},
					},
				},
			],
			"has more steps": tail,
		};
	}
	switch (operation.properties.operation.state.name) {
		case "contains": {
			head = {
				type: "wildcard",
				"has more steps": tail,
			};
			break;
		}
		case "ends with": {
			head = {
				type: "wildcard",
				"has more steps": tail,
			};
			break;
		}
		case "equals": {
			break;
		}
		case "starts with": {
			break;
		}
		default:
			unexpectedState(operation.properties.operation.state);
	}
	return head!;
}

export function createPropertiesDictionaryFromColumns(client_query: api_cs.Cquery__client_state): PropertiesCollection {
	const new_properties = client_query.properties.columns.toArray().reduce((res, val) => {
		const key = val[0];
		const $ = val[1];
		const property_path = $.key.ref.properties.path;
		$.properties.type.switch({
			number: ($) => {
				const node_path = mapNodePath(property_path).build();
				if (
					$.inferences.number_type().properties.number.ref.inferences.number().properties.ui.properties
						.dynamic_numerical_type.state.name === "yes"
				) {
					res[key + "idpath"] = {
						path: node_path,
						type: ["id path", {}],
					};
				}
				res[key] = {
					path: node_path,
					type: [
						"number",
						{
							expression: {
								type: [
									"number property",
									{
										"signed number property": {
											path: new NodePathBuilder().build(),
											sign: ["positive", {}],
											"property name": $.inferences.number_type().properties.number.entry,
										},
									},
								],
							},
						},
					],
				};
			},
			text: ($) => {
				res[key] = {
					path: mapNodePath(property_path).build(),
					type: [
						"text",
						{
							expression: {
								type: [
									"singular",
									{
										expression: {
											type: [
												"dynamic",
												{
													path: new NodePathBuilder().build(),
													text: $.inferences.text_type().properties.text.entry,
												},
											],
										},
									},
								],
							},
						},
					],
				};
			},
			file: ($) => {
				res[key] = {
					path: mapNodePath(property_path).build(),
					type: [
						"file",
						{
							file: $.inferences.file_type().properties.file.entry,
						},
					],
				};
			},
			"state group": ($) => {
				const node_path = mapNodePath(property_path).build();
				if (hasStyle($.inferences.state_group_type().properties.state_group.ref.inferences.state_group())) {
					res[key + "idpath"] = {
						path: node_path,
						type: ["id path", {}],
					};
				}
				res[key] = {
					path: node_path,
					type: [
						"state group",
						{
							"state group": $.inferences.state_group_type().properties.state_group.entry,
						},
					],
				};
			},
			widget: undefined,
		});
		return res;
	}, <api_cr.Tnode_query["properties"]>{});

	return new_properties;
}

export function createNodeExistanceFilter(path: api_cr.Tnode_path): api_cr.Tvalue_filters {
	return {
		path: path,
		type: [
			"node",
			{
				type: [
					"existence",
					{
						operator: ["exists", {}],
					},
				],
			},
		],
	};
}

export function createNodeContainmentFilter(
	node_path: api_cr.Tnode_path,
	id_path: IdPathBuilder
): api_cr.Tvalue_filters {
	return {
		path: node_path,
		type: [
			"node",
			{
				type: [
					"containment",
					{
						operator: ["in", {}],
						references: {
							"referenced node": id_path.build(),
							"has alternative": ["no", {}],
						},
					},
				],
			},
		],
	};
}

function createViewContextForQuery(query: api_cs.Cquery__client_state): IdPathBuilder {
	const position = query.input.position();
	switch (position.variant.name) {
		case "node": {
			const $ = position.variant.definition;
			return createIdPathBuilderFromNode($.input.entity().output.node());
		}
		case "navigable text property":
			return IdPathBuilder.create(); //FIXME is it possible to create an id path?
		default:
			unexpectedState(position.variant);
	}
}

export function createFiltersFromColumns(client_query: api_cs.Cquery__client_state): Filters {
	const view_context = createViewContextForQuery(client_query);
	let filters: api_cr.Tvalue_filters[] = [];
	client_query.properties.columns.forEach(function ($) {
		const node_path = mapNodePath($.key.ref.properties.path);
		$.properties.type.switch({
			number: function (n) {
				n.properties.has_filter.switch({
					no: undefined,
					yes: ($) =>
						$.properties.criteria_is_valid.switch({
							no: undefined,
							yes: (valid_criteria) =>
								$.properties.operator_selected.switch({
									no: undefined,
									yes: (operatord_selected) => {
										filters = filters.concat(createNodeExistanceFilter(node_path.build()));
										filters.push({
											path: node_path.build(),
											type: [
												"property",
												{
													property: n.inferences.number_type().properties.number.entry,
													type: [
														"number",
														{
															expression: {
																operator: operatord_selected.properties.operator.switch({
																	"equal to": () =>
																		[
																			"equals",
																			{
																				criterium: valid_criteria.properties.criteria.value,
																			},
																		] as api_cr.Tnumber_filter_expression["operator"],
																	"greater than": () =>
																		[
																			"range",
																			{
																				"greater than": [
																					"yes",
																					{
																						criterium: valid_criteria.properties.criteria.value,
																					},
																				],
																				"less than": rangeCriteriaLessThan($),
																			},
																		] as api_cr.Tnumber_filter_expression["operator"],
																	"greater than or equal to": () =>
																		[
																			"range",
																			{
																				"greater than": [
																					"yes",
																					{
																						criterium: valid_criteria.properties.criteria.value - 1,
																					},
																				],
																				"less than": rangeCriteriaLessThan($),
																			},
																		] as api_cr.Tnumber_filter_expression["operator"],
																	"smaller than": () =>
																		[
																			"range",
																			{
																				"greater than": rangeCriteriaGreaterThan($),
																				"less than": [
																					"yes",
																					{
																						criterium: valid_criteria.properties.criteria.value,
																					},
																				],
																			},
																		] as api_cr.Tnumber_filter_expression["operator"],
																	"smaller than or equal to": () =>
																		[
																			"range",
																			{
																				"greater than": rangeCriteriaGreaterThan($),
																				"less than": [
																					"yes",
																					{
																						criterium: valid_criteria.properties.criteria.value + 1,
																					},
																				],
																			},
																		] as api_cr.Tnumber_filter_expression["operator"],
																}),
															},
														},
													],
												},
											],
										});
									},
								}),
						}),
				});
			},
			text: function (t) {
				t.properties.has_filter.switch({
					no: undefined,
					yes: ($) => {
						$.properties.filter_set.switch({
							no: undefined,
							yes: ($) => {
								filters = filters.concat(createNodeExistanceFilter(node_path.build()));
								filters.push({
									path: node_path.build(),
									type: [
										"property",
										{
											property: t.inferences.text_type().properties.text.entry,
											type: [
												"text",
												{
													expression: buildTextExpression($.parent),
												},
											],
										},
									],
								});
							},
						});
					},
				});
				switch (t.properties.filter_on_current_id_path.state.name) {
					case "no":
						break;
					case "yes": {
						filters.push({
							path: node_path.reference(t.inferences.text_type().properties.text.entry).build(),
							type: [
								"node",
								{
									type: [
										"containment",
										{
											operator: "in",
											references: {
												"referenced node": view_context.build(),
												"has alternative": "no",
											},
										},
									],
								},
							],
						});
						break;
					}
					default:
						unexpectedState(t.properties.filter_on_current_id_path.state);
				}
			},
			file: undefined, //no file filters
			"state group": function (sg) {
				sg.properties.has_filter.switch({
					no: undefined,
					yes: function ($) {
						$.properties.filter_enabled.switch({
							no: undefined,
							yes: function () {
								const states_to_include: { [key: string]: api_cr.Tstates_to_include } = {};
								$.properties.states.forEach(function ($) {
									$.properties.is_selected.switch({
										yes: function () {
											states_to_include[$.key] = {};
										},
										no: undefined,
									});
								});

								filters = filters.concat(createNodeExistanceFilter(node_path.build()));

								const any_state_selected = Object.keys(states_to_include).length > 0;
								if (any_state_selected) {
									filters.push({
										path: node_path.build(),
										type: [
											"property",
											{
												property: sg.inferences.state_group_type().properties.state_group.ref.parent.parent.key.ref.key,
												type: [
													"state group",
													{
														"states to include": states_to_include,
													},
												],
											},
										],
									});
								}
							},
						});
					},
				});
			},
			widget: undefined,
		});
	});

	for (const [, filter] of client_query.properties.filters) {
		switch (filter.properties.active.state.name) {
			case "no":
				break;
			case "yes": {
				const filter_definition = filter.key.ref;
				const node_path = mapNodePath(filter_definition.properties.path);
				filters.push({
					path: node_path.build(),
					type: filter_definition.properties.type.switch<api_cr.Tvalue_filters["type"]>({
						node: ($) => [
							"node",
							{
								type: $.properties.type.switch<api_cr.Tnode["type"]>({
									existence: ($) => {
										return [
											"existence",
											{
												operator: $.properties.operator.switch({
													exists: "exists",
													"not exists": "not exists",
												}),
											},
										];
									},
									"view context": (): api_cr.Tnode["type"] => [
										"containment",
										{
											operator: "in",
											references: {
												"referenced node": view_context.build() as api_cr.Tid_path__client_request,
												"has alternative": "no",
											},
										},
									],
								}),
							},
						],
						property: ($) => [
							"property",
							{
								property: $.properties.property.entry,
								type: $.properties.type.switch<api_cr.Tproperty__type__value_filters["type"]>({
									collection: ($) => [
										"collection",
										{
											operator: $.properties.operator.switch({
												in: "in",
												"not in": "not in",
											}),
											keys: $.properties.keys.map(() => ({})),
										},
									],
									number: ($) => {
										const number_expression = $.properties.expression;
										return [
											"number",
											{
												expression: {
													operator: number_expression.properties.operator.switch<
														api_cr.Tnumber_filter_expression["operator"]
													>({
														equals: ($) => {
															return [
																"equals",
																{
																	criterium: $.properties.criterium.value,
																},
															];
														},
														range: ($) => [
															"range",
															{
																"greater than": $.properties.greater_than.switch<api_cr.Trange["greater than"]>({
																	no: "no",
																	yes: ($) => [
																		"yes",
																		{
																			criterium: $.properties.criterium.value,
																		},
																	],
																}),
																"less than": $.properties.less_than.switch<api_cr.Trange["less than"]>({
																	no: "no",
																	yes: ($) => [
																		"yes",
																		{
																			criterium: $.properties.criterium.value,
																		},
																	],
																}),
															},
														],
													}),
												},
											},
										];
									},
									"state group": ($) => [
										"state group",
										{
											"states to include": $.properties.states_to_include.map(() => ({})),
										},
									],
									text: ($) => {
										const [head] = buildTextExpressionFromString($.properties.text);
										return [
											"text",
											{
												expression: head!,
											},
										];
									},
								}),
							},
						],
					}),
				});

				break;
			}
			default:
				unexpectedState(filter.properties.active.state);
		}
	}

	return filters.reduce<Filters>((p, c, i) => ((p["" + i] = c), p), {});
}

function rangeCriteriaLessThan(number: api_cs.Cyes__has_filter__number): api_cr.Trange["less than"] {
	switch (number.properties.filter_range.state.name) {
		case "yes": {
			const range_filter = number.properties.filter_range.state.node;
			switch (range_filter.properties.criteria_is_valid.state.name) {
				case "yes": {
					switch (range_filter.properties.operator.state.name) {
						case "smaller than": {
							const smaller_than = range_filter.properties.operator.state.node;
							const criterium = range_filter.properties.criteria_is_valid.state.node.properties.criteria.value;
							switch (smaller_than.properties.or_equal_to.state.name) {
								case "yes": {
									return [
										"yes",
										{
											criterium: criterium + 1,
										},
									];
								}
								case "no": {
									return [
										"yes",
										{
											criterium: criterium,
										},
									];
								}
							}
							break;
						}
						case "greater than": {
							return "no";
						}
					}
					break;
				}
				case "no": {
					return "no";
				}
			}
			break;
		}
		case "no": {
			return "no";
		}
		case "not available": {
			return "no";
		}
	}
}

function rangeCriteriaGreaterThan(number: api_cs.Cyes__has_filter__number): api_cr.Trange["greater than"] {
	switch (number.properties.filter_range.state.name) {
		case "yes": {
			const range_filter = number.properties.filter_range.state.node;
			switch (range_filter.properties.criteria_is_valid.state.name) {
				case "yes": {
					switch (range_filter.properties.operator.state.name) {
						case "greater than": {
							const greater_than = range_filter.properties.operator.state.node;
							const criterium = range_filter.properties.criteria_is_valid.state.node.properties.criteria.value;
							switch (greater_than.properties.or_equal_to.state.name) {
								case "yes": {
									return [
										"yes",
										{
											criterium: criterium - 1,
										},
									];
								}
								case "no": {
									return [
										"yes",
										{
											criterium: criterium,
										},
									];
								}
							}
							break;
						}
						case "smaller than": {
							return "no";
						}
					}
					break;
				}
				case "no": {
					return "no";
				}
			}
			break;
		}
		case "no": {
			return "no";
		}
		case "not available": {
			return "no";
		}
	}
}
